import { useEffect, useState } from "react";
import PrimaryBtn from "@/components/Global/Button";
import { useFormik } from "formik";
import { companyProfileSchema } from "@/schema";
import Input from "@/components/Global/Input";
import { MdOutlineEmail } from "react-icons/md";
import { IoIosLink } from "react-icons/io";
import PhoneNumber from "@/components/Global/PhoneNumber";
import Editor from "@/components/Global/RichText";
import { SingleImageUplaod } from "@/components/Global/Image";
import { useMutation } from "@tanstack/react-query";
import { serverPATCH } from "@/utils/HttpFunctions";
import { errorNotfiy, successNotfiy } from "@/components/Global/Toast";
import { uploadToBucket } from "@/utils/CommonFunctions";
import { GoImage } from "react-icons/go";
import { useIsScrollable } from "@/utils/hooks/useIsScrollable";
import { AiOutlineDelete } from "react-icons/ai";
import useUserStore from "@/utils/Stores/userStore";
import Modal from "@/components/Global/Modal";
import { BiLock } from "react-icons/bi";
import ChangePassword from "@/components/Account/ChangePassword";
import { usePrompt } from "@/utils/hooks/usePrompt";

type inputTypes = {
  errors: any;
  touched: any;
  values: any;
  handleChange: any;
  handleSubmit: any;
  setFieldValue: any;
};

const CompanyProfile = () => {
  const [fetchedData, setFetchedData] = useState<any>(null);
  const isScrollable = useIsScrollable();
  const [passModal, setPassModal] = useState(false);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const user = useUserStore((state: any) => state.user);
  const setUser = useUserStore((state: any) => state?.setUser);
  const tenantID = useUserStore((state: any) => state.tenantId);
  const ownerTenant =
    user.email ==
    user.tenantID.find((item: any) => item._id?.toString() == tenantID)?.email;

  const [readOnlyError, setReadOnlyError] = useState(false);

  const handleToastClose = () => {};
  // const { data } = useQuery({
  //   queryKey: ["userInfo", user?._id], // Unique query key
  //   queryFn: () => serverGET(`/auth/info/` + user?._id), // Query function
  //   enabled: !!user, // Only run the query if id is truthy
  // });

  const { mutate: updateMutate, isPending: isUpdatePending } = useMutation({
    // mutationFn: (body: any) => serverPATCH("/auth/info/" + user._id, body),
    mutationFn: (body: any) => serverPATCH("/auth/tenant", body),

    onError: (error, variables, context) => {
      errorNotfiy(
        error?.message || "Something went wrong while updating company Info"
      );
      setFileUploadLoading(false);
    },
    onSuccess: (data, variables, context) => {
      successNotfiy("Success: Company Info Updated", handleToastClose);

      // const { email, isEmailVerified, is_active, name, _id, ...rest } = data;
      setUser({ ...user, company_details: data.company_details });
      setFileUploadLoading(false);
      setCustomDirty((customDirty) => !customDirty);
    },
  });

  const defaultValues = {
    company_name: "",
    company_website_link: "",
    company_email: "",
    company_phone_number: "",
    company_bio: "",
    personal_logo: "",
    personal_logo_file: "",
    company_logo: "",
    company_logo_file: "",
    address: [
      { address_title: "", address_line: "", city: "", state: "", zip: "" },
    ],
  };

  const initialValues = fetchedData
    ? Object.keys(defaultValues).reduce((acc, key) => {
        acc[key] = fetchedData[key] ?? defaultValues[key];
        return acc;
      }, {})
    : defaultValues;

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: companyProfileSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let temp: any = { ...values };
        // if (
        //   temp.personal_logo_file &&
        //   typeof temp.personal_logo_file !== "string"
        // ) {
        //   let uploadPersonalLogo = await uploadToBucket(
        //     [temp.personal_logo_file],
        //     "profile-pictures"
        //   );
        //   temp.personal_logo = uploadPersonalLogo[0].url;
        // }
        if (
          temp.company_logo_file &&
          typeof temp.company_logo_file !== "string"
        ) {
          setFileUploadLoading(true);
          let uploadCompanyLogo = await uploadToBucket(
            [temp.company_logo_file],
            "profile-pictures"
          );
          temp.company_logo = uploadCompanyLogo[0].url;
        }

        Object.keys(temp).forEach((key) => {
          if (
            temp[key] === "" ||
            temp[key] === null ||
            temp[key] === undefined ||
            key === "personal_logo_file" ||
            key == "company_logo_file"
          ) {
            delete temp[key];
          }
        });
        const body = {
          company_details: {
            name: temp.company_name,
            website: temp.company_website_link,
            phone_number: temp.company_phone_number,
            bio: temp.company_bio,
            logo: temp.company_logo,
            address: temp.address,
            email: temp.company_email,
          },
        };

        updateMutate(body);
      } catch (err) {
        console.log("Error", err);
      }
    },
  });
  useEffect(() => {
    if (user) {
      console.log("🚀 ~ useEffect ~ user:", user);
      setFetchedData({
        company_name: user?.company_details?.name || "",
        company_website_link: user?.company_details?.website || "",
        company_email: user?.company_details?.email || "",
        company_phone_number: user?.company_details?.phone_number || "",
        company_bio: user?.company_details?.bio || "",
        company_logo: user?.company_details?.logo || "",
        company_logo_file: "",
        address:
          user?.company_details?.address?.length > 0
            ? user?.company_details?.address
            : [
                {
                  address_title: "",
                  address_line: "",
                  city: "",
                  state: "",
                  zip: "",
                },
              ],
      });
    }
  }, [user]);

  // Deconstruct Formik object
  const { handleSubmit, dirty } = formik;
  const { PromptModal, setCustomDirty } = usePrompt(
    "You have unsaved changes. Are you sure you want to leave?",
    dirty,
    formik
  );
  //console.log("erros---", formik.errors);

  return (
    <div className=" relative w-11/12 mx-auto pt-8">
      {readOnlyError && (
        <>
          {errorNotfiy("You have the permission to view only", () =>
            setReadOnlyError(false)
          )}
        </>
      )}

      <PromptModal />
      <form onSubmit={handleSubmit}>
        <div className="flex justify-between">
          <p className="text-h1 text-black-secondary">Company Info</p>
          {ownerTenant && (
            <div className="my-2 ">
              <div className="flex justify-end text-center mb-4">
                <PrimaryBtn
                  type="submit"
                  text="Save"
                  disabled={isUpdatePending || fileUploadLoading}
                  loading={isUpdatePending || fileUploadLoading}
                />
              </div>
            </div>
          )}
        </div>

        <hr className="mb-3" />

        <div className="flex flex-col gap-4">
          {!ownerTenant && (
            <div
              className="absolute inset-0 bg-transparent z-50"
              onClick={() => setReadOnlyError(true)}
            />
          )}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <span className="block text-left text-sm font-semibold text-black-secondary">
                Company Profile
              </span>
            </div>
            <div className="col-span-3">
              <CompanyInfo {...formik} />
            </div>
          </div>
          <hr />

          <div className="grid grid-cols-4 gap-4">
            <div>
              <span className="block text-left text-sm font-semibold text-black-secondary">
                Address
              </span>
            </div>
            <div className="col-span-3">
              <Address {...formik} />
            </div>
          </div>
          <hr />
        </div>
        {/* <hr className="mb-3" /> */}

        {isScrollable && ownerTenant && (
          <div className="flex gap-3 justify-end py-6">
            <PrimaryBtn
              type="submit"
              text="Save"
              disabled={isUpdatePending}
              loading={isUpdatePending}
            />
          </div>
        )}
      </form>
      {passModal && (
        <Modal
          handleClose={() => setPassModal(false)}
          title="Change your password"
          description="Setup a new password to log in"
          icon={<BiLock />}
          width="max-w-sm"
          central
        >
          <ChangePassword handleClose={() => setPassModal(false)} />
        </Modal>
      )}
    </div>
  );
};

export function CompanyInfo({
  errors,
  touched,
  values,
  handleChange,
  setFieldValue,
  companyModal,
}: inputTypes & { isService?: boolean; companyModal?: boolean }) {
  const user = useUserStore((state: any) => state.user);

  const handlePhoneChange = (val: any) => {
    if (val) {
      setFieldValue(`company_phone_number`, val);
    } else {
      setFieldValue(`company_phone_number`, "");
    }
  };

  const handleAboutChange = (val: any) => {
    setFieldValue(`company_bio`, val);
  };

  return (
    <>
      <div>
        {companyModal ? (
          <div>
            <p className="pb-2 text-sm font-medium text-customGrey-icon">
              Company Logo
            </p>
            <SingleImageUplaod
              setFieldValue={setFieldValue}
              values={values}
              name="company_logo"
              label=""
              companyModal={true}
            />
          </div>
        ) : (
          <div className=" flex gap-x-4 mb-3">
            <div
              className={`rounded-full mt-3 max-w-[115px] ${!values.company_logo && !values.company_logo_file ? "w-fit" : "w-full"}  h-[115px] overflow-hidden`}
            >
              {!values.company_logo && !values.company_logo_file ? (
                <GoImage className="text-5xl text-customGreen-primary bg-customGreen-dark p-7 rounded-full w-fit h-fit" />
              ) : (
                <img
                  src={
                    values.company_logo_file
                      ? URL.createObjectURL(values.company_logo_file)
                      : values.company_logo
                  }
                  alt="company logo"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div>
              {user?.workSpaceOwner ? (
                <SingleImageUplaod
                  setFieldValue={setFieldValue}
                  values={values}
                  name="company_logo"
                  label="Upload your Company logo here."
                />
              ) : null}
            </div>
          </div>
        )}

        <div className={!companyModal ? `grid grid-cols-2 gap-4` : "space-y-2"}>
          <div>
            <Input
              label="Company Name"
              name="company_name"
              type="text"
              placeholder="Company Name"
              isDisabled={companyModal ? false : !user?.workSpaceOwner}
              value={values.company_name}
              onChange={handleChange}
              error={errors.company_name}
              touched={touched.company_name}
              required
            />
          </div>
          <div>
            <Input
              label="Website Link"
              name="company_website_link"
              type="text"
              placeholder="Website Link"
              value={values.company_website_link}
              onChange={handleChange}
              isDisabled={companyModal ? false : !user?.workSpaceOwner}
              error={errors.company_website_link}
              touched={touched.company_website_link}
              icon={<IoIosLink className="text-gray-500" />}
            />
          </div>
          {!companyModal && (
            <div>
              <Input
                label="Company Email"
                name="company_email"
                type="text"
                placeholder="Company Email"
                value={values.company_email}
                onChange={handleChange}
                isDisabled={!user?.workSpaceOwner}
                error={errors.company_email}
                touched={touched.company_email}
                icon={<MdOutlineEmail className="text-gray-500" />}
                autoComplete="companyEmail"
              />
            </div>
          )}
          <div>
            <span className="block text-sm font-medium text-black-secondary text-left mb-2">
              Phone Number
              <span className="px-1 text-red-500">*</span>
            </span>
            <div>
              <PhoneNumber
                phone={values.company_phone_number}
                handleChange={handlePhoneChange}
                error={errors.company_phone_number}
                touched={touched.company_phone_number}
                isDisabled={companyModal ? false : !user?.workSpaceOwner}
              />
            </div>
          </div>

          {!companyModal && (
            <div className=" col-span-2">
              <div>
                <span className="block text-sm font-medium text-black-secondary mb-2">
                  Bio
                </span>
              </div>
              <div className=" ">
                <Editor
                  placeholder="Add a Small Bio for your company"
                  value={values.company_bio}
                  handleChange={handleAboutChange}
                  isDisabled={!user?.workSpaceOwner}
                  maxHeight="h-32"
                  maxLength={275}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function Address({
  errors,
  touched,
  values,
  handleChange,
  setFieldValue,
}: inputTypes & { isService?: boolean }) {
  const user = useUserStore((state: any) => state.user);

  const addAddress = () => {
    setFieldValue("address", [
      ...values.address,
      { address_title: "", address_line: "", city: "", state: "", zip: "" },
    ]);
  };

  const customHandleChange = (e: any) => {
    let temp = [...values.address];
    temp[parseInt(e.target.name.split("-")[1])][e.target.name.split("-")[0]] =
      e.target.value;
    setFieldValue("address", temp);
  };

  const handleDeleteAddress = (index: number) => {
    setFieldValue(
      "address",
      values.address?.filter((_, i) => i !== index)
    );
  };
  return (
    <>
      <div>
        {values.address.map((address: any, index: number) => (
          <div
            className=" relative grid grid-cols-3 gap-4 mb-3 border border-customGrey-border2 rounded-lg p-4"
            key={index}
          >
            <div
              className={`absolute -right-2 -top-2 text-customRed-lightLabel cursor-pointer bg-error-100 rounded-full w-6 h-6 flex items-center justify-center  ${
                values.address.length == 1 ? " hidden " : " visible"
              }`}
            >
              <AiOutlineDelete
                onClick={() => {
                  handleDeleteAddress(index);
                }}
              />
            </div>

            <div>
              <Input
                label="Name"
                name={`address_title-${index}`}
                type="text"
                placeholder="Name"
                value={address.address_title}
                onChange={customHandleChange}
                required={true}
                error={errors?.address?.[index]?.address_title}
                touched={touched?.address?.[index]?.address_title}
                isDisabled={!user?.workSpaceOwner}
                autoComplete="name"
              />
            </div>
            <div className="col-span-2">
              <Input
                label="Address Line"
                name={`address_line-${index}`}
                type="text"
                placeholder="Address Line"
                value={address.address_line}
                onChange={customHandleChange}
                required={true}
                error={errors?.address?.[index]?.address_line}
                touched={touched?.address?.[index]?.address_line}
                isDisabled={!user?.workSpaceOwner}
                autoComplete="street-address"
              />
            </div>
            <div>
              <Input
                label="City"
                name={`city-${index}`}
                type="text"
                placeholder="City"
                value={address.city}
                onChange={customHandleChange}
                error={errors?.address?.[index]?.city}
                touched={touched?.address?.[index]?.city}
                isDisabled={!user?.workSpaceOwner}
              />
            </div>
            <div>
              <Input
                label="State"
                name={`state-${index}`}
                type="text"
                placeholder="State"
                value={address.state}
                onChange={customHandleChange}
                error={errors?.address?.[index]?.state}
                touched={touched?.address?.[index]?.state}
                isDisabled={!user?.workSpaceOwner}
              />
            </div>
            <div>
              <Input
                label="Zip"
                name={`zip-${index}`}
                type="text"
                placeholder="Zip"
                value={address.zip}
                onChange={customHandleChange}
                error={errors?.address?.[index]?.zip}
                touched={touched?.address?.[index]?.zip}
                isDisabled={!user?.workSpaceOwner}
              />
            </div>
          </div>
        ))}
        {user?.workSpaceOwner && (
          <button
            type="button"
            className=" text-sm text-brand-700 font-medium mt-4"
            onClick={() => addAddress()}
          >
            Add Additional Address
          </button>
        )}
      </div>
    </>
  );
}
export default CompanyProfile;
